import type { DragEndEvent, Modifier } from '@dnd-kit/core'
import { DndContext } from '@dnd-kit/core'
import { restrictToWindowEdges } from '@dnd-kit/modifiers'
import type { Transform } from '@dnd-kit/utilities'
import { ThemeProvider } from '@material-ui/core/styles'
import cx from 'classnames'
import { runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import { useAppStore } from '@src/app/context'
import { isWindows } from '@src/config'
import useInstance from '@src/lib/hooks/useInstance'
import useWindowSize from '@src/lib/hooks/useWindowSize'
import { useAppTheme } from '@src/theme'
import ClickAwayListener from '@ui/ClickAwayListener'
import { themeClassNames } from '@ui/theme'
import { LayerProvider } from '@ui/z-index'

import * as styles from './CallsDialog.css'
import IncomingCalls from './IncomingCalls'
import OngoingCalls from './OngoingCalls'
import CallsDialogProvider from './context/CallsDialogProvider'
import CallDialogController from './store/CallDialogController'

const restrictToWindowEdgesAccountForTopPadding =
  (paddingTop: number): Modifier =>
  (props): Transform => {
    const { windowRect, ...rest } = props

    if (!windowRect) {
      return rest.transform
    }

    return restrictToWindowEdges({
      ...rest,
      windowRect: {
        ...windowRect,
        top: windowRect.top + paddingTop,
        height: windowRect.height - paddingTop,
      },
    })
  }

const CallsDialog = () => {
  const { service, isElectron } = useAppStore()
  const theme = useAppTheme('dark')
  const wrapper = useRef<HTMLDivElement | null>(null)
  const blurred = !service.voice.focused
  const calls = service.voice.calls.list
  const windowSize = useWindowSize()
  const callDialogController = useInstance(
    () => new CallDialogController(service),
    [service],
  )

  const { setCoordinates } = callDialogController

  const handleDragEnd = (event: DragEndEvent) => {
    const rect = event.active.rect.current.translated
    if (rect) {
      setCoordinates({
        left: rect.left,
        top: rect.top,
        right: window.innerWidth - rect.right,
        bottom: window.innerHeight - rect.bottom,
      })
    }
  }

  const focusWrapper = () => {
    wrapper?.current?.focus()
  }

  const handleBoundaries = () => {
    const children = wrapper?.current?.children
    if (children) {
      Array.from(children).forEach((_, i) => {
        const element = children[i] as HTMLElement
        const { top, left } = element.getBoundingClientRect()
        if (top < 0) {
          element.style.top = ''
          element.style.bottom = ''
        }
        if (left < 0) {
          element.style.left = ''
          element.style.right = ''
        }
      })
    }
  }

  const setFocused = useCallback(
    (focused: boolean) => {
      runInAction(() => {
        // eslint-disable-next-line react-compiler/react-compiler -- UXP-3732 - Fix React Compiler errors
        service.voice.focused = focused
      })
    },
    [service.voice],
  )

  useEffect(() => {
    if (calls.length) {
      focusWrapper()
      setFocused(true)
    }
  }, [calls, service.voice, setFocused])

  useEffect(() => {
    handleBoundaries()
  }, [windowSize])

  const restrictWindowEdgesModifier = useMemo(() => {
    let padding = 0

    if (isElectron) {
      // setts a padding for the top of the window
      // to avoid any collisions with draggable elements
      padding += 85

      // on windows, we need to account for the title bar
      if (isWindows) {
        padding += 32
      }
    }

    return restrictToWindowEdgesAccountForTopPadding(padding)
  }, [isElectron])

  return (
    <ThemeProvider theme={theme}>
      <DndContext
        onDragEnd={handleDragEnd}
        onDragStart={focusWrapper}
        modifiers={[restrictWindowEdgesModifier]}
      >
        <CallsDialogProvider blurred={blurred}>
          <ClickAwayListener onClickAway={() => setFocused(false)}>
            <div
              ref={wrapper}
              onClick={() => setFocused(true)}
              onFocus={() => setFocused(true)}
              tabIndex={0}
              className={cx(styles.root, themeClassNames.dark)}
            >
              <LayerProvider layer="callDialog">
                <IncomingCalls
                  className={cx(styles.calls, { [styles.callsBlurred]: blurred })}
                />
                <OngoingCalls
                  className={cx(styles.calls, { [styles.callsBlurred]: blurred })}
                  callDialogController={callDialogController}
                  onModeChange={handleBoundaries}
                />
              </LayerProvider>
            </div>
          </ClickAwayListener>
        </CallsDialogProvider>
      </DndContext>
    </ThemeProvider>
  )
}

export default observer(CallsDialog)
